import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {BenchlingState} from 'redux-stores/reduceApp';
import Button, {ButtonProps} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import {isCustomParty} from 'ui-library/entity-input/EntityInputUtil';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionData} from 'ui-library/menus/MenuConstants';
import APIUtil from 'util/APIUtil';
import * as Keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, InputElementFocusEvent} from 'util/Types';

import {PartyInputQuery, PartyInputQueryVariables} from './__generated/PartyInputQuery';
import {CustomParty, EntitySuggestion, MinimalEntity, PARTY_INPUT_QUERY} from './EntityInputConstants';
import {EntitySuggestionRow, getPartyDisplayName} from './EntitySuggestionRow';

export interface EntityInputProps {
  allOptions: Array<EntitySuggestion | CustomParty> | null;
  forceDisplayOptionsOnFocus?: boolean;
  value: Array<MinimalEntity | CustomParty>;
  placeholder: string;
  query?: string;
  shouldAllowMultiple?: boolean;
  autoFocus?: boolean;
  className?: string;
  chipSize?: ChipSize;
  /**
   * The id to be passed to the input element for label association.
   */
  id?: string;

  // A function to filter the entities. Should return a list of entities that should be
  // rendered as options. You are given the user's text input as well as the full list of options
  filterOptions?: (
    text: string,
    options: Array<EntitySuggestion | CustomParty>
  ) => Array<EntitySuggestion | CustomParty>;

  // A filter function to determine which entities should be disabled.
  disableOptionsFilter?: (text: string, option: EntitySuggestion | CustomParty) => boolean;

  // An optional button rendered inline next to the input. This should reflect the action the
  // user is taking on submit.
  buttonProps?: ButtonProps;

  // If no entities match, render emptyText in the dropdown.
  emptyText: ReactNode;

  // A function called when the user changes the input text, which is typically the entity's
  // handle or an email. The parent component can use this to monitor input changes, and possibly
  // modify the `allOptions` value in response.
  onChangeInput?: (text: string) => void;
  onChange: (value: Array<EntitySuggestion | CustomParty>) => void;
  // A function called when the user hits "Enter" when it is not meaningful in the context of
  // this component. E.g. if the user has selected an option and has not entered additional text
  onEnter?: () => void;
  onFocus?: (e: InputElementFocusEvent) => void;
  // Function called to handle special key strokes.
  onKeyDown?: (e: ElementKeyboardEvent) => void;

  disabled?: boolean;
  isReadOnly?: boolean; // TODO(BNCH-57668): remove isReadOnly when disabled functionality is fixed
}

const renderSuggestion = (optionData: OptionData): JSX.Element => (
  <EntitySuggestionRow key={optionData.value.id} suggestion={optionData.value} isSmallInput={false} />
);

interface ConnectedProps {
  getDisplayName: (e: MinimalEntity | CustomParty) => string;
}

function mapStateToProps(state: BenchlingState): ConnectedProps {
  function getDisplayName(entity: MinimalEntity | CustomParty): string {
    if (isCustomParty(entity)) {
      return entity.text;
    }

    const entitySummariesByHandle = state.models.managed.entitySummariesByHandle ?? {};
    const userName = (entitySummariesByHandle[entity.handle]?.name ?? '') || getPartyDisplayName(entity);

    // Some entities we receive are broken, and come in with only an ID, no handle. We try to manually look them
    // up in the store as a final attempt to find the right name.
    if ((!userName || !entity.handle) && APIUtil.apiIdToMaybeType(entity.id) === ModelTypeConstants.ENTITY) {
      const matchingHandle = Object.keys(entitySummariesByHandle).find(
        (handle) => entitySummariesByHandle[handle].id === entity.id
      );
      if (matchingHandle) {
        return entitySummariesByHandle[matchingHandle].name || getPartyDisplayName(entity);
      }
    }
    return userName;
  }

  return {getDisplayName};
}

function ChipInput({
  chipsOptions,
  chipsValue,
  ...props
}: Omit<
  EntityInputProps & {
    chipsOptions: Array<EntitySuggestion | CustomParty> | null;
    chipsValue: Array<EntitySuggestion | CustomParty>;
  },
  'value' | 'allOptions'
> &
  ConnectedProps): JSX.Element {
  const optionFilter = props.filterOptions ?? ((_, options) => options);
  const optionDatas = optionFilter(props.query ?? '', chipsOptions ?? []).map((suggestion) => ({
    value: suggestion,
    name: props.getDisplayName(suggestion),
    disabled: props.disableOptionsFilter ? props.disableOptionsFilter(props.query ?? '', suggestion) : false,
  }));
  const hasMatches = Boolean(optionDatas.length);

  const noMatchingOption = {
    name: <div className={css(styles.noMatches)}>{props.emptyText}</div>,
    disabled: true,
  };

  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
    if (props.onEnter) {
      Keyboard.callOnEnter(props.onEnter)(e);
    }
  };

  const resultsOptionsDatas = hasMatches ? optionDatas : [noMatchingOption];

  const shouldDisplayResultsWhenFocused =
    chipsOptions && Boolean(props.query || props.forceDisplayOptionsOnFocus);
  return (
    <ChipSelectInput
      className={props.className}
      optionDatas={shouldDisplayResultsWhenFocused ? resultsOptionsDatas : []}
      value={chipsValue}
      placeholder={props.placeholder}
      query={props.query}
      isMulti={props.shouldAllowMultiple}
      // Default autoFocus to false to match LegacyTagsInput behavior in LegacyEntityInput
      autoFocus={props.autoFocus ?? false}
      button={props.buttonProps && <Button type="button" {...props.buttonProps} />}
      id={props.id}
      getChipProps={(optionValue) => ({
        id: optionValue.id,
        text: optionValue.text,
        isLink: false,
        size: props.chipSize ?? ChipSize.SMALL,
      })}
      renderOptionContent={hasMatches ? renderSuggestion : (optionData: OptionData) => optionData.name}
      onFocus={props.onFocus}
      onKeyDown={handleKeyDown}
      onQueryChange={(e) => {
        if (props.onChangeInput) {
          props.onChangeInput(e.target.value);
        }
      }}
      onSelect={(entity) => {
        if (props.onChangeInput) {
          props.onChangeInput('');
        }
        props.onChange([...chipsValue, entity]);
      }}
      onRemove={(optionValue: EntitySuggestion | CustomParty) => {
        props.onChange(
          chipsValue.filter((value) => {
            if (isCustomParty(value) && isCustomParty(optionValue)) {
              return value.text !== optionValue.text;
            } else if (isCustomParty(value) || isCustomParty(optionValue)) {
              return true;
            }
            return value.id !== optionValue.id;
          })
        );
      }}
      disabled={props.disabled}
      isReadOnly={props.isReadOnly}
    />
  );
}

/**
 * Allows the user to select an Entity from the given list of options, which can include custom parties (e.g.
 * NULL_PARTY). If you don't have a list already, consider using EntityInputWithSearch, which performs API
 * queries to search based on the user's text input.
 */
function EntityInput(props: EntityInputProps & ConnectedProps): JSX.Element {
  const {value, allOptions, getDisplayName, ...otherProps} = props;
  const optionPartyIds =
    allOptions?.filter((option) => !isCustomParty(option)).map((option: EntitySuggestion) => option.id) ?? [];
  const valuePartyIds = value.filter((value) => !isCustomParty(value)).map((v: MinimalEntity) => v.id);
  const allPartyIds = [...new Set([...optionPartyIds, ...valuePartyIds])].filter((id) => id != null);

  return (
    <BenchlingQuery<PartyInputQuery, PartyInputQueryVariables>
      query={PARTY_INPUT_QUERY}
      variables={{ids: allPartyIds}}
      renderLoading={() => (
        <ChipInput
          chipsOptions={allOptions?.map((opt) => ({...opt, text: getDisplayName(opt)})) ?? null}
          chipsValue={value.map((v) => ({
            ...v,
            text: getDisplayName(v),
          }))}
          getDisplayName={getDisplayName}
          {...otherProps}
        />
      )}
    >
      {(data) => {
        const validParties = accessibleObjs(data.parties);
        const partyById = _.indexBy(validParties, 'id');
        const hydratedOptions =
          allOptions?.map((opt) => {
            if (isCustomParty(opt)) {
              return opt;
            } else {
              return {
                ...opt,
                ...partyById[opt.id],
                text: getDisplayName(partyById[opt.id] || opt),
              } as EntitySuggestion;
            }
          }) ?? null;

        const hydratedValue = value.map((v: MinimalEntity | CustomParty) => {
          if (isCustomParty(v)) {
            return v;
          } else {
            return {
              ...v,
              ...partyById[v.id],
              text: getDisplayName(partyById[v.id] || v),
            };
          }
        });
        return (
          <ChipInput
            chipsOptions={hydratedOptions}
            chipsValue={hydratedValue}
            getDisplayName={getDisplayName}
            {...otherProps}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  noMatches: {
    width: '100%',
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default connect(mapStateToProps)(EntityInput);
